export const ASSET_TYPES = [
  { value: 'LIFE_INSURANCE', label: 'Life insurance' },
  { value: 'HEALTH_INSURANCE', label: 'Health insurance' },
  { value: 'MUTUAL_FUND', label: 'Mutual fund' },
  { value: 'BANK_FD', label: 'Bank FD / savings' },
  { value: 'PPF', label: 'PPF / post office' },
  { value: 'PROPERTY', label: 'Property / real estate' },
  { value: 'OTHER', label: 'Other asset' },
];

export const INSURANCE_PROVIDERS = [
  'LIC',
  'HDFC Life',
  'ICICI Prudential',
  'SBI Life',
  'Max Life',
];

export const BANK_PROVIDERS = [
  'Axis Bank',
  'HDFC Bank',
  'ICICI Bank',
  'SBI',
  'Kotak Mahindra Bank',
];

export const MUTUAL_FUND_PROVIDERS = [
  'Nippon India Mutual Fund',
  'HDFC Mutual Fund',
  'ICICI Prudential Mutual Fund',
  'SBI Mutual Fund',
];

export const PPF_PROVIDERS = [
  'Post Office',
  'SBI',
  'HDFC Bank',
  'ICICI Bank',
  'Axis Bank',
];

export const PROVIDER_OTHER = 'Other';

export const PROVIDERS_BY_ASSET_TYPE = {
  LIFE_INSURANCE: INSURANCE_PROVIDERS,
  HEALTH_INSURANCE: INSURANCE_PROVIDERS,
  MUTUAL_FUND: MUTUAL_FUND_PROVIDERS,
  BANK_FD: BANK_PROVIDERS,
  PPF: PPF_PROVIDERS,
  PROPERTY: [],
  OTHER: [],
};

/** Flat list of every preset provider (excluding Other) — for legacy lookups */
export const PROVIDER_PRESETS = [
  ...new Set([
    ...INSURANCE_PROVIDERS,
    ...BANK_PROVIDERS,
    ...MUTUAL_FUND_PROVIDERS,
    ...PPF_PROVIDERS,
  ]),
  PROVIDER_OTHER,
];

export function getProvidersForAssetType(assetType) {
  if (!assetType) return [];
  const providers = PROVIDERS_BY_ASSET_TYPE[assetType] || [];
  return [...providers, PROVIDER_OTHER];
}

export function isProviderValidForAssetType(assetType, providerName) {
  if (!providerName) return false;
  if (providerName === PROVIDER_OTHER) return Boolean(assetType);
  return getProvidersForAssetType(assetType).includes(providerName);
}

export function normalizeNomineePhoneForKey(phone) {
  return (phone || '').replace(/\D/g, '');
}

export function getNomineeContactKey(nominee) {
  const email = (nominee?.nomineeEmail || '').trim().toLowerCase();
  const phone = normalizeNomineePhoneForKey(nominee?.nomineePhone);
  return `${email}|${phone}`;
}

export function getUniqueNominees(nominees = []) {
  const seen = new Map();
  nominees.forEach((nominee) => {
    const key = getNomineeContactKey(nominee);
    if (!seen.has(key)) {
      seen.set(key, { ...nominee, assetCount: 1 });
      return;
    }
    const existing = seen.get(key);
    existing.assetCount += 1;
    if (!existing.nomineeName?.trim() && nominee.nomineeName?.trim()) {
      existing.nomineeName = nominee.nomineeName;
    }
  });
  return Array.from(seen.values());
}

/** Dropdown label — phone first when email repeats, so similar entries stand out. */
export function getNomineeOptionLabel(nominee, { includeAssetCount = true } = {}) {
  const name = nominee?.nomineeName?.trim();
  const email = nominee?.nomineeEmail?.trim();
  const phone = nominee?.nomineePhone?.trim();

  const contactParts = phone && email ? `${phone} · ${email}` : [email, phone].filter(Boolean).join(' · ');
  let label;
  if (name && contactParts) {
    label = `${name} · ${contactParts}`;
  } else if (name) {
    label = name;
  } else if (contactParts) {
    label = contactParts;
  } else {
    label = 'Nominee';
  }

  if (includeAssetCount && nominee?.assetCount > 1) {
    label += ` (${nominee.assetCount} assets)`;
  }
  return label;
}

export function resolveProviderPreset(providerName, assetType = null) {
  const trimmed = (providerName || '').trim();
  if (!trimmed) return PROVIDER_OTHER;
  const searchList = assetType
    ? getProvidersForAssetType(assetType)
    : PROVIDER_PRESETS;
  const match = searchList.find(
    (preset) => preset.toLowerCase() === trimmed.toLowerCase()
  );
  return match || PROVIDER_OTHER;
}

export function validateOptionalEmail(email) {
  const trimmed = (email || '').trim();
  if (!trimmed) return '';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) return 'Please enter a valid email address.';
  if (trimmed.length > 100) return 'Email address must be less than 100 characters.';
  if (trimmed.includes('..')) return 'Email address cannot contain consecutive dots.';
  return '';
}

export function validateOptionalPhone(phone) {
  const trimmed = (phone || '').trim();
  if (!trimmed) return '';
  if (!/^[0-9]{10}$/.test(trimmed)) return 'Phone number must be exactly 10 digits.';
  if (trimmed.startsWith('0')) return 'Phone number should not start with 0.';
  if (trimmed.startsWith('1')) return 'Phone number should not start with 1.';
  if (/^(.)\1{9}$/.test(trimmed)) return 'Phone number cannot have all same digits.';
  return '';
}

export function validateContactRequirement(email, phone) {
  if (!(email || '').trim() && !(phone || '').trim()) {
    return 'Provide at least an email or phone number for the nominee.';
  }
  return '';
}

export function validateNomineeName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return 'Nominee name is required.';
  if (trimmed.length < 2) return 'Nominee name must be at least 2 characters.';
  if (trimmed.length > 80) return 'Nominee name must be less than 80 characters.';
  return '';
}

export function validateAssetType(assetType) {
  if (!assetType) return 'Please select an asset type.';
  return '';
}

export function validateProviderName(name) {
  if (!name) return 'Provider name is required.';
  const trimmedName = name.trim();
  if (trimmedName.length < 2) return 'Provider name must be at least 2 characters.';
  if (trimmedName.length > 50) return 'Provider name must be less than 50 characters.';
  if (!/^[a-zA-Z0-9\s&.-]+$/.test(trimmedName)) {
    return 'Provider name can only contain letters, numbers, spaces, &, ., and -.';
  }
  return '';
}

export function validatePolicyNumber(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (trimmed.length > 40) return 'Policy or account number must be less than 40 characters.';
  return '';
}

export function validateFile(file, isRequired = true) {
  if (!file && isRequired) return 'Please select a policy or asset document.';
  if (!file) return '';

  const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
  const maxSize = 2 * 1024 * 1024;
  const minSize = 1024;

  if (!allowedTypes.includes(file.type)) {
    return 'Only PDF, JPG, and PNG files are allowed.';
  }
  if (file.size > maxSize) {
    return `File size must be less than 2MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`;
  }
  if (file.size < minSize) {
    return 'File seems too small. Please upload a valid document.';
  }
  return '';
}

export function getAssetTypeLabel(value) {
  return ASSET_TYPES.find((item) => item.value === value)?.label || value || 'Asset';
}
